import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { DeleteIcon, DownloadIcon, PreviewIcon, InfoIcon, UploadIcon } from '../../../../../../../bombiix-icon';
import type { BombiixCellRendererParams } from '../../../../../../../Components/BombiixAGGrid/types';
import type { MeasurementGridRow } from '../grid-types';

/** Glyph size inside the `.bombiix-icon-container` chip — 20px here, so the chip grows with the glyph. */
const ICON_SIZE = 20;
/** Preview / Download / Info / Delete — the slots Upload has to clear to stay in the same column. */
const DOC_ACTION_COUNT = 4;

/** Handlers are injected through the column's cellProps by MeasurementsGrid. */
interface DocActionsCellProps extends BombiixCellRendererParams<MeasurementGridRow> {
  onUpload?: (row: MeasurementGridRow) => void;
  onPreview?: (row: MeasurementGridRow) => void;
  onDownload?: (row: MeasurementGridRow) => void;
  onDelete?: (row: MeasurementGridRow) => void;
}

/**
 * Supporting-document actions. Preview/Download/Info/Delete appear on each document row; the Upload
 * button appears on the product's first row (so a product with no documents can still add one) and
 * always renders last. Every chip sits in a fixed slot: a row missing the document actions renders
 * empty stand-ins in their place, so Upload keeps the same column on every row at one uniform gap.
 */
const DocActionsCell: React.FC<DocActionsCellProps> = ({ data, onUpload, onPreview, onDownload, onDelete }) => {
  const { t } = useTranslation('bulk-edit');
  if (!data) return null;
  if (!data.isFirstOfProduct && !data.hasDoc) return null;
  const hasDocument = Boolean(data.documentId);
  /* Created-by / Modified-by name + date, mirroring ProductTesting.showCreatedandModified. */
  const formatDate = (value: string): string => (value ? moment(value).format('DD-MMM-YYYY, h:mm a') : '');
  const createdDetail = [data.createdBy, formatDate(data.createdAt)].filter(Boolean).join(' | ');
  const modifiedDetail = data.modifiedBy ? [data.modifiedBy, formatDate(data.modifiedAt)].filter(Boolean).join(' | ') : '';
  /* Master-design info popover (bombiix-info-popover): Created / Modified as a dt/dd grid. */
  const infoPopover = (
    <Popover id={`bulk-measure-doc-info-${data.documentId || data.id}`} className="bombiix-info-popover">
      <Popover.Content>
        <dl>
          <dt>{t('MEASUREMENTS_MODULE.CREATED')}</dt>
          <dd>{createdDetail || '—'}</dd>
          {modifiedDetail && (
            <>
              <dt>{t('MEASUREMENTS_MODULE.MODIFIED')}</dt>
              <dd>{modifiedDetail}</dd>
            </>
          )}
        </dl>
      </Popover.Content>
    </Popover>
  );

  return (
    <div className="bulk-measure-doc-actions">
      {data.hasDoc && (
        <>
          <button
            type="button"
            className="bombiix-icon-container bombiix-icon-container--blue"
            aria-label={t('MEASUREMENTS_MODULE.ARIA_PREVIEW_DOCUMENT')}
            disabled={!hasDocument}
            onClick={() => onPreview?.(data)}
          >
            <PreviewIcon size={ICON_SIZE} />
          </button>
          <button
            type="button"
            className="bombiix-icon-container bombiix-icon-container--blue"
            aria-label={t('MEASUREMENTS_MODULE.ARIA_DOWNLOAD_DOCUMENT')}
            disabled={!hasDocument}
            onClick={() => onDownload?.(data)}
          >
            <DownloadIcon size={ICON_SIZE} />
          </button>
          <OverlayTrigger placement="top" trigger={['hover', 'focus']} overlay={infoPopover} rootClose>
            <span
              className="bombiix-icon-container bombiix-icon-container--blue"
              tabIndex={0}
              role="button"
              aria-label={t('MEASUREMENTS_MODULE.ARIA_DOCUMENT_DETAILS')}
            >
              <InfoIcon size={ICON_SIZE} />
            </span>
          </OverlayTrigger>
          <button
            type="button"
            className="bombiix-icon-container bombiix-icon-container--red"
            aria-label={t('MEASUREMENTS_MODULE.ARIA_DELETE_DOCUMENT')}
            disabled={!hasDocument}
            onClick={() => onDelete?.(data)}
          >
            <DeleteIcon size={ICON_SIZE} />
          </button>
        </>
      )}
      {/* A row with no document renders no action chips, so hold their slots open — Upload then lands in
          the same column as it does on a row that has them, at one uniform gap from its neighbours. */}
      {data.isFirstOfProduct &&
        !data.hasDoc &&
        Array.from({ length: DOC_ACTION_COUNT }, (_value, slot) => (
          <span key={`slot-${slot}`} className="bulk-measure-doc-actions__slot" aria-hidden="true" />
        ))}
      {data.isFirstOfProduct && (
        <button
          type="button"
          className="bombiix-icon-container bombiix-icon-container--blue"
          aria-label={t('MEASUREMENTS_MODULE.UPLOAD_DOCUMENT')}
          onClick={() => onUpload?.(data)}
        >
          <UploadIcon size={ICON_SIZE} />
        </button>
      )}
    </div>
  );
};

export default DocActionsCell;
